import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Grid,
    Box,
    Card,
    CardContent,
    Avatar,
    Chip,
    Stack,
    Divider,
    IconButton,
} from "@mui/material";
import {
    Close as CloseIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationOnIcon,
    DateRange as DateRangeIcon,
    Business as BusinessIcon,
    AttachFile as AttachFileIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { useGetAdminWarrantyDetailsQuery } from "../../../store/warranty/warrantyApis";
import { baseUrl } from "../../../helpers/constants";
import { handleDateTimeDefault } from "../../../helpers/utils";

const WarrantyDetailsDialog = ({ open, onClose, warrantyId }) => {
    const { data: warrantyDetails, isLoading } = useGetAdminWarrantyDetailsQuery(warrantyId, {
        skip: !warrantyId || !open,
    });

    const formatFileSize = (bytes) => {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    if (isLoading) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>Warranty Registration Details</DialogTitle>
                <DialogContent>
                    <Typography>Loading...</Typography>
                </DialogContent>
            </Dialog>
        );
    }

    if (!warrantyDetails) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>Warranty Registration Details</DialogTitle>
                <DialogContent>
                    <Typography>No details found.</Typography>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Warranty Registration Details</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3}>
                    {/* Header with Registration Number and Status */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h5" fontWeight="bold">
                            {warrantyDetails.registration_number}
                        </Typography>
                    </Box>

                    <Divider />

                    {/* Customer Information */}
                    <Card variant="outlined">
                        <CardContent>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                            >
                                <PersonIcon />
                                Customer Information
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Full Name
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {warrantyDetails.customer_name}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Mobile Number
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {warrantyDetails.mobile}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Email Address
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {warrantyDetails.email}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Pin Code
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {warrantyDetails.pincode}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                        Address
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {warrantyDetails.address}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        City
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {warrantyDetails.city}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        State
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {warrantyDetails.state}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Product Information */}
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Product Information
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={4}>
                                    {warrantyDetails.product?.image_url && (
                                        <Avatar
                                            src={`${baseUrl}${warrantyDetails.product.image_url}`}
                                            alt={warrantyDetails.product.name}
                                            sx={{ width: 80, height: 80 }}
                                            variant="rounded"
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <Typography variant="body2" color="text.secondary">
                                        Product Name
                                    </Typography>
                                    <Typography variant="h6" fontWeight="medium">
                                        {warrantyDetails.product_name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Description
                                    </Typography>
                                    <Typography variant="body1">
                                        {warrantyDetails.product?.description || "No description available"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Purchase Information */}
                    <Card variant="outlined">
                        <CardContent>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                            >
                                <BusinessIcon />
                                Purchase Information
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Purchase Date
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {warrantyDetails.purchase_date
                                            ? new Date(warrantyDetails.purchase_date).toLocaleDateString()
                                            : "N/A"}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Dealer Name
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {warrantyDetails.dealer_name}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                        Dealer State
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {warrantyDetails.dealer_state}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Files */}
                    {(() => {
                        let filesArray = [];
                        try {
                            // Handle both string and array formats
                            if (typeof warrantyDetails.files === "string") {
                                filesArray = JSON.parse(warrantyDetails.files);
                            } else if (Array.isArray(warrantyDetails.files)) {
                                filesArray = warrantyDetails.files;
                            }
                        } catch (error) {
                            console.error("Error parsing files:", error);
                            filesArray = [];
                        }

                        return (
                            filesArray.length > 0 && (
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            sx={{ display: "flex", alignItems: "center", gap: 1 }}
                                        >
                                            <AttachFileIcon />
                                            Attached Files ({filesArray.length})
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {filesArray.map((file, index) => {
                                                // Handle both string URLs and file objects
                                                const fileUrl = typeof file === "string" ? file : file.uri || file.url;
                                                const fileName =
                                                    typeof file === "string"
                                                        ? `File ${index + 1}`
                                                        : file.name || `File ${index + 1}`;

                                                return (
                                                    <Grid item xs={12} sm={6} key={index}>
                                                        <Card variant="outlined" sx={{ p: 2 }}>
                                                            <Stack spacing={1}>
                                                                <Typography variant="body2" fontWeight="medium">
                                                                    {fileName}
                                                                </Typography>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    href={fileUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    View File
                                                                </Button>
                                                            </Stack>
                                                        </Card>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            )
                        );
                    })()}

                    {/* Status Information */}
                    {(warrantyDetails.approved_by || warrantyDetails.rejection_reason) && (
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Status Information
                                </Typography>
                                <Grid container spacing={2}>
                                    {warrantyDetails.approved_by && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="text.secondary">
                                                Approved By
                                            </Typography>
                                            <Typography variant="body1" fontWeight="medium">
                                                {warrantyDetails.approved_by}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {warrantyDetails.rejection_reason && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="text.secondary">
                                                Rejection Reason
                                            </Typography>
                                            <Typography variant="body1" fontWeight="medium" color="error">
                                                {warrantyDetails.rejection_reason}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                    )}

                    {/* Registration Information */}
                    <Card variant="outlined">
                        <CardContent>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                            >
                                <DateRangeIcon />
                                Registration Information
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Registration Date
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {handleDateTimeDefault(warrantyDetails.created_at)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Last Updated
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {handleDateTimeDefault(warrantyDetails.updated_at)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WarrantyDetailsDialog;
